import { HeaderComponent } from '@app-bareka/components/header/header.component';
import { ActivatedRoute, Event, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { Component, effect, HostListener, OnDestroy, OnInit, inject, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationService } from 'projects/shared/src/lib/core/services/navigation.service';
import { AuthStore } from 'projects/shared/src/lib/store/auth.store';
import { ENVIRONMENT_TOKEN } from 'projects/shared/src/lib/core/injection-tokens/environment.token';
import { SpeechService } from 'projects/shared/src/lib/core/services/speech.service';
import { LanguageService } from 'projects/shared/src/lib/core/services/language.service';
import { SignalRService } from 'projects/shared/src/lib/core/services/signalr.service';
import {
  ApplicationIdentificator,
  isApplicationMatch,
  SettingsStore,
} from 'projects/shared/src/lib/store/settings.store';
import { filter, Subject, takeUntil } from 'rxjs';
import { RouterAuthEnum } from 'projects/shared/src/lib/infrastructure/enums/router-link.enum';
import { VoiceLoadDialogComponent } from 'projects/shared/src/lib/components/voice-load-dialog/voice-load-dialog.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, FormsModule, VoiceLoadDialogComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  // Temporarily block that logout functionality to avoid of potential problems since the browser event order does not garantee the correct behavior
  // @HostListener('window:beforeunload')
  // beforeUnloadHandler() {
  //   if (this.authStore.authInfo().isAuthenticated) {
  //     sessionStorage.setItem('isUnloading', 'true');
  //   } else {
  //     sessionStorage.removeItem('isUnloading');
  //   }
  // }

  // @HostListener('window:unload')
  // unloadHandler() {
  //   if (sessionStorage.getItem('isUnloading')) {
  //     //this.forceLogout();
  //   }
  // }

  // @HostListener('window:DOMContentLoaded')
  // domContentLoadedHandler() {
  //   sessionStorage.removeItem('isUnloading');
  // }

  public showHeader = signal<boolean>(true);

  private readonly environment = inject(ENVIRONMENT_TOKEN);
  private readonly authStore = inject(AuthStore);
  private readonly settingsStore = inject(SettingsStore);
  private readonly navigationService = inject(NavigationService);
  private readonly speechService = inject(SpeechService);
  private readonly languageService = inject(LanguageService);
  private readonly signalRService = inject(SignalRService);
  public readonly router = inject(Router);
  public readonly route = inject(ActivatedRoute);

  /** Subject that emits when the component is destroyed, used to complete subscriptions */
  private readonly destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    this.languageService.initialize();
    await this.authStore.refreshAuthInfo();
    if (this.authStore.isAuthenticated()) {
      await this.initUser();
      this.navigationService.navigateToHomePage();
    } else {
      await this.authStore.authorize();
      if (this.authStore.isAuthenticated()) {
        await this.initUser();
        this.navigationService.navigateToHomePage();
      } else {
        this.authStore.logoutLocal();
        await this.authStore.authorize();
      }
    }
    this.route.url.subscribe(([url]) => {
      if (window.location.pathname.startsWith(`/${RouterAuthEnum.LoggedOut}`))
        this.showHeader.set(false);
    });

    await this.speechService.initalize().catch((err) => {
      console.error('Error initializing speech service: ', err);
    });
  }

  async forceLogout() {
    const duarationEndpoint = `${this.environment.baseApiUrl}/auth/token/revocation`;
    const body = JSON.stringify({
      spaClientId: this.environment.identityClientId,
      tokens: [
        { token: this.authStore.authInfo().accessToken, tokenTypeHint: 'access_token' },
        { token: this.authStore.authInfo().refreshToken, tokenTypeHint: 'refresh_token' },
      ],
    });
    try {
      const authInfo = this.authStore.authInfo();
      this.authStore.logoutLocal();
      fetch(duarationEndpoint, {
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authInfo.accessToken}`,
        },
        keepalive: true,
      });
    } catch (e) {
      console.error(`Failed to send revocation request: ${e}`);
    }
  }

  private async initUser() {
    const account = await this.authStore.requestAccount();
    this.languageService.initialize(account?.identityId);
    await this.settingsStore.loadSettings(ApplicationIdentificator.Bareka);

    // Connect to SignalR for real-time settings updates
    if (account?.identityId) {
      await this.initSignalRConnection(account.identityId);
    }
  }

  /**
   * Initializes the SignalR connection for receiving real-time settings updates.
   */
  private async initSignalRConnection(userIdentityId: string): Promise<void> {
    // Subscribe BEFORE starting the connection so a push that arrives during the
    // connect handshake can't slip through the gap (settingsUpdated$/reconnected$
    // are plain Subjects with no replay). takeUntil ensures cleanup on destroy.
    this.signalRService.settingsUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        // Only apply settings if they are for Bareka application
        if (isApplicationMatch(settings.application, ApplicationIdentificator.Bareka)) {
          if (!this.environment.production)
            console.log('[Bareka] Applying pushed settings update:', settings);
          this.settingsStore.applyPushedSettings(settings);
        }
      });

    // After a reconnect, refetch settings to reconcile any pushes lost while
    // disconnected (SignalR is fire-and-forget; the Azure SignalR Service does not buffer).
    this.signalRService.reconnected$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.environment.production)
          console.log('[Bareka] Reconnected - refetching settings to reconcile');
        this.settingsStore.loadSettings(ApplicationIdentificator.Bareka);
      });

    try {
      await this.signalRService.startConnection(userIdentityId);
    } catch (error) {
      console.error('[Bareka] Failed to initialize SignalR connection:', error);
      // Non-critical failure - the app can still work without real-time updates
    }
  }

  ngOnDestroy(): void {
    // Complete the destroy$ subject to unsubscribe all takeUntil subscriptions
    this.destroy$.next();
    this.destroy$.complete();
    this.signalRService.stopConnection();
  }
}
